import React, { useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { setMapLocation } from "@src/core/controllers";
import { DroneEntity, DroneStatusType, getDroneStatusDisplay } from "@src/core/features";
import { routesConfig } from "@src/core/routes";
import { ArcIconButton, ArcStack, ArcText } from "@src/core/ui";
import { useOrderProvider, useFlightProvider } from "@src/features/order/provider";
import { OrderMissionEntity } from "@src/features/order/entities";
import { setMissionId, setSelectedDrone } from "@src/features/drone/redux/actions";
import { selectSelectedDrone } from "@src/features/drone/redux/selectors";
import { arcToFixed } from "@src/core/extensions";
import { ReactComponent as ArrowLeftIcon } from "@src/assets/icons/arrow-left.svg";
import { DRONE_CARD_STYLES, CAROUSEL_STYLES, POPUP_BUTTON_STYLES, ArrowBackIosTight } from "./styles";

const MAX_VISIBLE = 3;

const formatStatus = (
  drone: DroneEntity,
  getProgress: (d: DroneEntity) => number | null
): string => {
  if (drone.status === DroneStatusType.MISSION) {
    const p = getProgress(drone);
    if (p !== null) {
      return `MISSION (${parseFloat(arcToFixed(p, 1))}%)`;
    }
  }
  return getDroneStatusDisplay(drone.status).label;
};

const EnRouteTopDronesCarousel: React.FC<{
  missionDrone?: DroneEntity;
  viewer?: boolean;
}> = ({ missionDrone, viewer }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { missionId: routeMissionId } = useParams<{ missionId: string }>();
  const [, setSearchParams] = useSearchParams();
  const selectedFromRedux = useSelector(selectSelectedDrone);
  const { orders, isLoading: ordersLoading } = useOrderProvider();
  const { flightData } = useFlightProvider();
  const carouselRef = useRef<HTMLDivElement>(null);
  const [previousProgress, setPreviousProgress] = React.useState<Record<string, number>>({});
  const [showPopup, setShowPopup] = React.useState(false);
  const popupRef = useRef<HTMLDivElement>(null);

  const enRouteMissions = React.useMemo(
    () => orders?.en_route ?? [],
    [orders?.en_route]
  );

  React.useEffect(() => {
    if (flightData?.serialNumber && typeof flightData?.missionProgress === "number") {
      const s = flightData.serialNumber;
      const v = flightData.missionProgress;
      setPreviousProgress((prev) => (prev[s] === v ? prev : { ...prev, [s]: v }));
    }
  }, [flightData]);

  const getProgress = React.useCallback(
    (drone: DroneEntity): number | null => {
      if (flightData?.serialNumber === drone.serialNumber && typeof flightData?.missionProgress === "number") {
        return flightData.missionProgress;
      }
      return previousProgress[drone.serialNumber] ?? null;
    },
    [flightData, previousProgress]
  );

  const activeDrone = selectedFromRedux ?? missionDrone ?? null;

  const sortedMissions = React.useMemo(() => {
    const list = [...enRouteMissions];
    if (list.length === 0) return list;
    const atUrl = list.find((m) => m.missionId === routeMissionId);
    if (atUrl) {
      return [atUrl, ...list.filter((m) => m.missionId !== routeMissionId)];
    }
    if (activeDrone) {
      const cur = list.find((m) => m.drone.id === activeDrone.id);
      if (cur) {
        return [cur, ...list.filter((m) => m.missionId !== cur.missionId)];
      }
    }
    return list;
  }, [enRouteMissions, routeMissionId, activeDrone]);

  const isCardSelected = (m: OrderMissionEntity) => {
    if (routeMissionId && m.missionId === routeMissionId) return true;
    if (activeDrone) return m.drone.id === activeDrone.id;
    return false;
  };

  const missionPathBase = viewer ? routesConfig.enRouteViewer : routesConfig.takeOffToEnRoute;

  const onSelect = React.useCallback(
    (m: OrderMissionEntity) => {
      const drone = m.drone;
      setShowPopup(false);
      dispatch(setMissionId(undefined));
      dispatch(
        setMapLocation({
          latitude: Number(drone.latitude),
          longitude: Number(drone.longitude),
          address: drone.name,
        })
      );
      dispatch(setSelectedDrone(drone));
      if (m.missionId && m.missionId !== routeMissionId) {
        navigate(
          {
            pathname: `${missionPathBase}/${m.missionId}`,
            search: `?${new URLSearchParams({ droneSerial: drone.serialNumber })}`,
          },
          { replace: true }
        );
      } else {
        setSearchParams((prev) => {
          const n = new URLSearchParams(prev);
          n.set("droneSerial", drone.serialNumber);
          return n;
        });
      }
    },
    [dispatch, missionPathBase, navigate, routeMissionId, setSearchParams]
  );

  const onBack = () => {
    dispatch(setSelectedDrone(null));
    setSearchParams((prev) => {
      const n = new URLSearchParams(prev);
      n.delete("droneSerial");
      return n;
    });
    navigate(viewer ? routesConfig.viewer : routesConfig.orders, { replace: true });
  };

  const visible = sortedMissions.slice(0, MAX_VISIBLE);
  const remaining = sortedMissions.slice(MAX_VISIBLE);
  const remainingCount = remaining.length;

  if (ordersLoading) {
    return null;
  }

  if (enRouteMissions.length < 1) {
    return null;
  }

  return (
    <ArcStack direction="row" spacing={1.2} alignItems="center">
      <ArcStack direction="row" alignItems="center" spacing={1} sx={{ flexShrink: 0 }}>
        <ArcIconButton onClick={onBack}>
          <ArrowLeftIcon width={18} />
        </ArcIconButton>
      </ArcStack>
      <ArcStack
        ref={carouselRef}
        direction="row"
        spacing={1.2}
        alignItems="center"
        sx={{ ...CAROUSEL_STYLES, overflowX: "auto", overflowY: "visible" }}
        className="no-scrollbar"
      >
        {visible.map((m) => {
          const drone = m.drone;
          const isSel = isCardSelected(m);
          return (
            <ArcStack
              key={m.missionId}
              borderColor={isSel ? "brand.main" : "transparent"}
              onClick={() => onSelect(m)}
              bgcolor="background.paper"
              sx={DRONE_CARD_STYLES}
            >
              <ArcText.caption
                fontSize={12}
                whiteSpace="nowrap"
                fontWeight={400}
                color="white"
                style={{ overflow: "hidden", textOverflow: "ellipsis" }}
              >
                {drone.name}
              </ArcText.caption>
              <ArcText.caption
                fontSize={10}
                whiteSpace="nowrap"
                fontWeight={500}
                style={{ overflow: "hidden", textOverflow: "ellipsis" }}
              >
                {formatStatus(drone, getProgress)}
              </ArcText.caption>
            </ArcStack>
          );
        })}
        {remainingCount > 0 && (
          <ArcStack
            borderColor="transparent"
            onClick={() => setShowPopup(true)}
            bgcolor="background.paper"
            sx={{
              ...POPUP_BUTTON_STYLES,
              minWidth: "max-content",
              flexShrink: 0,
              "&:hover": { bgcolor: "grey.800" },
            }}
          >
            {showPopup ? (
              <ArrowBackIosTight sx={{ color: "text.secondary", fontSize: 15 }} />
            ) : (
              <ArcText.caption fontSize={12} whiteSpace="nowrap" fontWeight={400} color="white" minWidth="15px">
                +{remainingCount}
              </ArcText.caption>
            )}
          </ArcStack>
        )}
      </ArcStack>

      {showPopup && remainingCount > 0 && (
        <>
          <ArcStack
            position="fixed"
            top={0}
            left={0}
            right={0}
            bottom={0}
            zIndex={99}
            sx={{ cursor: "pointer" }}
            onClick={() => setShowPopup(false)}
          />
          <ArcStack
            p="10px"
            width="100%"
            maxWidth="fit-content"
            borderRadius={1}
            position="absolute"
            left={480}
            top={53}
            sx={{ maxHeight: "70vh", overflowY: "auto" }}
            zIndex={100}
            bgcolor="background.default"
          >
            <ArcStack ref={popupRef} spacing={1.2}>
              {remaining.map((m) => (
                <ArcStack
                  key={m.missionId}
                  borderColor={isCardSelected(m) ? "brand.main" : "transparent"}
                  onClick={() => onSelect(m)}
                  bgcolor="background.paper"
                  sx={DRONE_CARD_STYLES}
                >
                  <ArcText.caption fontSize={12} whiteSpace="nowrap" fontWeight={400} color="white">
                    {m.drone.name}
                  </ArcText.caption>
                  <ArcText.caption fontSize={10} whiteSpace="nowrap" fontWeight={500}>
                    {formatStatus(m.drone, getProgress)}
                  </ArcText.caption>
                </ArcStack>
              ))}
            </ArcStack>
          </ArcStack>
        </>
      )}
    </ArcStack>
  );
};

export default EnRouteTopDronesCarousel;
